import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';
import { REGISTER_COPY } from '@eliadmin/core/src/components/dlz/auth/register/register-copy';
import type { RegisterFormValues } from '@eliadmin/core/src/components/dlz/auth/register/register-schema';
import {
  normalizePhone,
  splitFullName,
  splitStreet,
} from '@eliadmin/core/src/components/dlz/auth/register/register-values';

export type CustomerUpdate = Omit<Partial<Customer>, 'id'>;

/** Body of `register`: the only fields the endpoint accepts (core `registerSchema`). */
export function toRegisterBody(values: RegisterFormValues, captcha: string | undefined) {
  return {
    name: values.name,
    surname: values.surname,
    email: values.email,
    phone: normalizePhone(values.phone),
    password: values.password,
    password_confirmation: values.password_confirmation,
    gdpr: values.consent,
    tos: values.consent,
    newsletter: values.newsletter,
    newsletter_return_url: values.newsletter_return_url,
    'g-recaptcha-response': captcha ?? '',
  };
}

function toCountry(id: string, countries: Country[]) {
  return { id, name: countries.find(country => country.id === id)?.name ?? id };
}

/**
 * Everything `register` does not take, as the customer record the checkout and the profile read
 * (`cart-order-form`: address / street_number / postcode / city / country, company, ico, dic,
 * ic_dph, `*_delivery`). The billing address is skipped while it is empty (the preselected
 * country alone is no data); the company persona always has something to save.
 */
export function toCustomerUpdate(
  values: RegisterFormValues,
  countries: Country[],
  defaultCountry: string,
): CustomerUpdate | null {
  const update: CustomerUpdate = {};
  const isCompany = values.persona === 'company';

  if (isCompany) {
    update.company_buy = true;
    update.company = values.company;
    update.ico = values.ico;
    update.dic = values.dic;
    if (values.ic_dph) update.ic_dph = values.ic_dph;
    update.email_company = values.contact_email;
  }

  if (values.address || values.city || values.postcode || values.country !== defaultCountry) {
    const { street, number } = splitStreet(values.address);
    if (street) update.address = street;
    if (number) update.street_number = number;
    if (values.city) update.city = values.city;
    if (values.postcode) update.postcode = values.postcode;
    if (values.country) update.country = toCountry(values.country, countries);
  }

  if (values.different_delivery_address) {
    const { street, number } = splitStreet(values.delivery_address);
    update.address_delivery = street;
    if (number) update.street_number_delivery = number;
    update.city_delivery = values.delivery_city;
    update.postcode_delivery = values.delivery_postcode;
    update.country_delivery = { id: values.delivery_country };

    if (isCompany) {
      update.company_delivery = values.delivery_company;
      const contact = splitFullName(values.delivery_contact);
      if (contact.name) update.name_delivery = contact.name;
      if (contact.surname) update.surname_delivery = contact.surname;
    }
  }

  return Object.keys(update).length ? update : null;
}

/** Fields of a 422 from `register` / `update` → fields of this form (the API names are the customer's). */
export const SERVER_FIELD_MAP: Record<string, keyof RegisterFormValues> = {
  name: 'name',
  surname: 'surname',
  email: 'email',
  phone: 'phone',
  password: 'password',
  password_confirmation: 'password_confirmation',
  gdpr: 'consent',
  tos: 'consent',
  newsletter: 'newsletter',
  address: 'address',
  street_number: 'address',
  city: 'city',
  postcode: 'postcode',
  'country.id': 'country',
  company: 'company',
  ico: 'ico',
  dic: 'dic',
  ic_dph: 'ic_dph',
  email_company: 'contact_email',
  company_delivery: 'delivery_company',
  name_delivery: 'delivery_contact',
  surname_delivery: 'delivery_contact',
  address_delivery: 'delivery_address',
  street_number_delivery: 'delivery_address',
  city_delivery: 'delivery_city',
  postcode_delivery: 'delivery_postcode',
  'country_delivery.id': 'delivery_country',
};

/** Body of the optional request for the company account (third consent of the company design). */
export function toBusinessAccountRequest(values: RegisterFormValues) {
  return {
    subject: REGISTER_COPY.businessAccountRequest.subject,
    text: REGISTER_COPY.businessAccountRequest.text,
    email: values.contact_email,
    name: values.name,
    surname: values.surname,
    phone: normalizePhone(values.phone),
    custom: { company: values.company, ico: values.ico, dic: values.dic, ic_dph: values.ic_dph },
    gdpr: values.consent,
  };
}
